import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Barcode, Plus, Warning, UsersThree, SealCheck, Eye, Package } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BinScanner } from "@/components/BinScanner";
import { MultiImageField } from "@/components/MultiImageField";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const field = "rounded-none border-slate-300 text-sm";

export default function PartsGuard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [station, setStation] = useState({ name: "", line: "", where: "", shift: "day" });
  const [part, setPart] = useState({ name: "", unit: "each", unit_value: "", barcode: "", household_usable: true, risk: "high" });
  const [issue, setIssue] = useState({ station_id: "", operator_id: "", shift: "day", job_ref: "" });
  const [kit, setKit] = useState({});                 // part_id -> qty
  const [count, setCount] = useState(null);           // { kit, lines: {}, photos: [] }
  const [sign, setSign] = useState({ talk_key: "the-rules", signature: "" });
  const [note, setNote] = useState({});               // shortage_id -> note
  const [watch, setWatch] = useState({ subject_id: "", reason: "" });
  const [draw, setDraw] = useState({ part_id: "", qty: "", job_ref: "", for_work_only: false });

  const load = useCallback(() => {
    api.get("/parts-guard").then(({ data: d }) => setData(d)).catch(oops);
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  const subscribe = async (tier, cycle) => {
    try {
      const { data: out } = await api.post("/payments/parts-guard/subscribe", {
        tier, cycle, origin_url: window.location.origin,
      });
      window.location.href = out.url;
    } catch (e) { oops(e); }
  };

  const addByBarcode = (code) => {
    const found = (data?.parts || []).find((p) => p.barcode === code);
    if (!found) return toast.error(`No part on the register with barcode ${code}`);
    setKit((k) => ({ ...k, [found.id]: Number(k[found.id] || 0) + 1 }));
    toast.success(`${found.name} added to the kit`);
  };

  const kitLines = () => Object.entries(kit)
    .filter(([, q]) => Number(q) > 0)
    .map(([part_id, qty]) => ({ part_id, qty: Number(qty) }));

  const openCount = (k) => setCount({
    kit: k, photos: [],
    lines: Object.fromEntries(k.lines.map((l) => [l.part_id, { fitted: "", returned: "", scrapped: "", reason: "" }])),
  });

  const submitCount = () => act(async () => {
    await api.post(`/parts-guard/issues/${count.kit.id}/return`, {
      lines: Object.entries(count.lines).map(([part_id, v]) => ({
        part_id, fitted: Number(v.fitted) || 0, returned: Number(v.returned) || 0,
        scrapped: Number(v.scrapped) || 0, reason: v.reason,
      })),
      scrap_image_ids: count.photos.map((p) => p.image_id),
    });
    setCount(null);
  }, "Counted back in");

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Opening the parts integrity desk…</p>;

  const sym = data.symbol || "$";

  return (
    <div className="space-y-6" data-testid="parts-guard-page">
      <div>
        <p className="overline text-[#0B5C2E]">Parts integrity</p>
        <h1 className="font-display mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
          <ShieldCheck size={30} weight="duotone" className="text-[#0B5C2E]" /> Nothing leaves uncounted
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="parts-guard-explainer">
          Kits are scanned out to a named operator at a named station for one shift, and counted
          back in at the end of it. Fitted, returned and scrapped has to equal what went out —
          anything left over needs a reason, and scrap goes back with a photograph. Repeat
          unexplained shortages land on the watchlist.
        </p>
      </div>

      {/* ---- Plan ---- */}
      <section className="border border-[#002FA7] bg-[#EEF3FF] p-4 sm:p-5" data-testid="guard-plan-card">
        <p className="overline text-[#002FA7]">Your plan</p>
        <p className="font-display mt-1 text-2xl font-bold text-[#0F172A]" data-testid="guard-plan-state">
          {data.plan.state === "subscribed"
            ? `${data.plans[data.plan.tier].label} · paid to ${data.plan.until}`
            : data.plan.state === "trial"
              ? `Free trial · ${data.plan.days_left} day${data.plan.days_left === 1 ? "" : "s"} left`
              : "Trial finished — pick a plan to carry on"}
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          {Object.entries(data.plans).map(([key, plan]) => (
            <div key={key} className="border border-border bg-white p-3" data-testid={`guard-plan-${key}`}>
              <p className="font-display text-xl font-bold text-[#0F172A]">${plan.price}<span className="text-xs font-normal text-slate-500">/month</span></p>
              <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                {plan.includes.map((l) => <li key={l}>{l}</li>)}
              </ul>
              <div className="mt-2 flex gap-2">
                <Button size="sm" disabled={busy} onClick={() => subscribe(key, "monthly")}
                  data-testid={`guard-subscribe-${key}-monthly`} className="rounded-none">Monthly</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => subscribe(key, "yearly")}
                  data-testid={`guard-subscribe-${key}-yearly`} className="rounded-none">Yearly</Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- What the plant is losing, shown to everybody ---- */}
      <section className="border border-border bg-white p-4" data-testid="guard-summary">
        <p className="overline text-slate-500">The last 30 days, openly</p>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {[["Shortages", data.summary.shortages_30d, "guard-summary-shortages"],
            ["Unaccounted for", data.summary.unexplained_30d, "guard-summary-unexplained"],
            ["Value lost", `${sym}${data.summary.value_30d}`, "guard-summary-value"],
            ["Kits out now", data.summary.open_kits, "guard-summary-open"],
            ["Parts on register", data.summary.parts_on_register, "guard-summary-parts"]].map(([l, v, t]) => (
            <div key={t} className="border border-border p-3" data-testid={t}>
              <p className="font-display text-xl font-bold text-[#0F172A]">{v}</p>
              <p className="text-xs text-slate-500">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---- My kits ---- */}
      <section className="border border-border bg-white p-4" data-testid="guard-my-kits">
        <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">Signed out to me</h2>
        {(data.my_kits || []).length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Nothing is signed out to you right now.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {data.my_kits.map((k) => (
              <div key={k.id} className="border border-border p-3" data-testid={`guard-kit-${k.id}`}>
                <p className="text-sm font-semibold text-[#0F172A]">
                  {k.station_name} · {k.shift} shift · {k.day}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {k.lines.map((l) => `${l.qty} ${l.unit} ${l.name}`).join(" · ")}
                </p>
                {k.status === "open" ? (
                  <Button size="sm" className="mt-2 rounded-none" data-testid={`guard-count-open-${k.id}`}
                    onClick={() => openCount(k)}>Count it back in</Button>
                ) : (
                  <p className="mt-1 text-xs font-semibold text-[#0B5C2E]">
                    Counted back in{k.short_lines ? ` · ${k.short_lines} line(s) short` : " · all present"}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---- The shift-end count ---- */}
      {count && (
        <section className="border-2 border-[#FF9500] bg-[#FFF8EE] p-4" data-testid="guard-count-form">
          <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">
            Shift-end count — {count.kit.station_name}
          </h2>
          <div className="mt-3 space-y-3">
            {count.kit.lines.map((l) => {
              const v = count.lines[l.part_id];
              const short = Number(l.qty) - (Number(v.fitted) || 0) - (Number(v.returned) || 0) - (Number(v.scrapped) || 0);
              return (
                <div key={l.part_id} className="border border-border bg-white p-3" data-testid={`guard-count-line-${l.part_id}`}>
                  <p className="text-sm font-semibold text-[#0F172A]">{l.name} — {l.qty} {l.unit} out</p>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {["fitted", "returned", "scrapped"].map((f) => (
                      <div key={f}>
                        <p className="text-[0.65rem] uppercase tracking-wider text-slate-500">{f}</p>
                        <Input type="number" min="0" className={field} value={v[f]}
                          data-testid={`guard-count-${f}-${l.part_id}`}
                          onChange={(e) => setCount({ ...count, lines: { ...count.lines, [l.part_id]: { ...v, [f]: e.target.value } } })} />
                      </div>
                    ))}
                  </div>
                  {short > 0 && (
                    <div className="mt-2" data-testid={`guard-count-short-${l.part_id}`}>
                      <p className="text-xs font-semibold text-[#FF3B30]">
                        {short} {l.unit} unaccounted for — give a reason
                      </p>
                      <select className="mt-1 w-full border border-slate-300 bg-white p-2 text-sm"
                        data-testid={`guard-count-reason-${l.part_id}`} value={v.reason}
                        onChange={(e) => setCount({ ...count, lines: { ...count.lines, [l.part_id]: { ...v, reason: e.target.value } } })}>
                        <option value="">Pick a reason…</option>
                        {data.reasons.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-3">
            <p className="text-xs text-slate-500">Photograph of the scrap going back (needed on Pro and above)</p>
            <MultiImageField images={count.photos} max={4} testid="guard-scrap-photos"
              onChange={(photos) => setCount({ ...count, photos })} />
          </div>
          <div className="mt-3 flex gap-2">
            <Button disabled={busy} onClick={submitCount} className="rounded-none" data-testid="guard-count-submit">
              Close the shift
            </Button>
            <Button variant="outline" className="rounded-none" data-testid="guard-count-cancel"
              onClick={() => setCount(null)}>Cancel</Button>
          </div>
        </section>
      )}

      {/* ---- Consumables off the crib ---- */}
      <section className="border border-border bg-white p-4" data-testid="guard-consumables">
        <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
          <Package size={18} weight="duotone" className="text-[#002FA7]" /> Consumables off the crib
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Tape, gloves, blades, spray and batteries are drawn against a fair monthly allowance
          and declared for a job on the line. Over the allowance it waits for a supervisor.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-4">
          <select className="border border-slate-300 bg-white p-2 text-sm" data-testid="guard-draw-part"
            value={draw.part_id} onChange={(e) => setDraw({ ...draw, part_id: e.target.value })}>
            <option value="">What are you taking…</option>
            {(data.consumables || []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.allowance_per_month} {p.unit} a month
              </option>
            ))}
          </select>
          <Input type="number" min="0" className={field} placeholder="How many" data-testid="guard-draw-qty"
            value={draw.qty} onChange={(e) => setDraw({ ...draw, qty: e.target.value })} />
          <Input className={field} placeholder="Job it is for" data-testid="guard-draw-job"
            value={draw.job_ref} onChange={(e) => setDraw({ ...draw, job_ref: e.target.value })} />
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" data-testid="guard-draw-declare" checked={draw.for_work_only}
              onChange={(e) => setDraw({ ...draw, for_work_only: e.target.checked })} />
            This is for work, not for home
          </label>
        </div>
        <Button size="sm" disabled={busy} className="mt-2 rounded-none" data-testid="guard-draw-submit"
          onClick={() => act(async () => {
            await api.post("/parts-guard/draws", { ...draw, qty: Number(draw.qty) || 0 });
            setDraw({ part_id: "", qty: "", job_ref: "", for_work_only: false });
          }, "Logged against your allowance")}>
          Take it
        </Button>
        {(data.my_draws || []).length > 0 && (
          <div className="mt-3 space-y-1" data-testid="guard-my-draws">
            {data.my_draws.map((d) => (
              <p key={d.id} className="text-xs text-slate-600" data-testid={`guard-draw-${d.id}`}>
                {d.day} · {d.qty} {d.unit} {d.part_name} · {d.status.replace("_", " ")}
                {d.allowance ? ` · allowance ${d.allowance}/month` : ""}
              </p>
            ))}
          </div>
        )}
      </section>

      {/* ---- Toolbox talks ---- */}
      <section className="border border-border bg-white p-4" data-testid="guard-training">
        <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
          <SealCheck size={18} weight="duotone" className="text-[#0B5C2E]" /> Toolbox talks and the honesty policy
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          {data.all_signed ? "All signed — thank you." : "Read each one and sign it. It takes two minutes."}
        </p>
        <div className="mt-3 space-y-3">
          {data.talks.map((t) => (
            <div key={t.key} className="border border-border p-3" data-testid={`guard-talk-${t.key}`}>
              <p className="text-sm font-semibold text-[#0F172A]">
                {t.title}
                {data.signed_talks.includes(t.key) && <span className="ml-2 text-xs font-semibold text-[#0B5C2E]">signed</span>}
              </p>
              <ul className="mt-1 space-y-1 text-sm text-slate-700">
                {t.points.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
              {!data.signed_talks.includes(t.key) && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Input className={`${field} max-w-xs`} placeholder="Type your name to sign"
                    data-testid={`guard-sign-input-${t.key}`}
                    value={sign.talk_key === t.key ? sign.signature : ""}
                    onChange={(e) => setSign({ talk_key: t.key, signature: e.target.value })} />
                  <Button size="sm" disabled={busy} className="rounded-none"
                    data-testid={`guard-sign-${t.key}`}
                    onClick={() => act(() => api.post("/parts-guard/training/sign", { talk_key: t.key, signature: sign.signature }), "Signed")}>
                    Sign it
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ---- Supervisor and security only ---- */}
      {data.can_see_reports && (
        <>
          {data.can_manage && (
            <section className="border border-border bg-white p-4" data-testid="guard-setup">
              <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">Lines, stations and the parts register</h2>
              <div className="mt-3 grid gap-2 sm:grid-cols-4">
                <Input className={field} placeholder="Station name" data-testid="guard-station-name"
                  value={station.name} onChange={(e) => setStation({ ...station, name: e.target.value })} />
                <Input className={field} placeholder="Line" data-testid="guard-station-line"
                  value={station.line} onChange={(e) => setStation({ ...station, line: e.target.value })} />
                <Input className={field} placeholder="Town or city (any country)" data-testid="guard-station-where"
                  value={station.where} onChange={(e) => setStation({ ...station, where: e.target.value })} />
                <select className="border border-slate-300 bg-white p-2 text-sm" data-testid="guard-station-shift"
                  value={station.shift} onChange={(e) => setStation({ ...station, shift: e.target.value })}>
                  {data.shifts.map((s) => <option key={s} value={s}>{s} shift</option>)}
                </select>
              </div>
              <Button size="sm" disabled={busy} className="mt-2 rounded-none" data-testid="guard-station-add"
                onClick={() => act(() => api.post("/parts-guard/stations", station), "Station set up")}>
                <Plus size={14} className="mr-1" /> Add the station
              </Button>

              <div className="mt-4 grid gap-2 sm:grid-cols-5">
                <Input className={field} placeholder="Part name" data-testid="guard-part-name"
                  value={part.name} onChange={(e) => setPart({ ...part, name: e.target.value })} />
                <Input className={field} placeholder="Unit" data-testid="guard-part-unit"
                  value={part.unit} onChange={(e) => setPart({ ...part, unit: e.target.value })} />
                <Input className={field} type="number" placeholder="Value each" data-testid="guard-part-value"
                  value={part.unit_value} onChange={(e) => setPart({ ...part, unit_value: e.target.value })} />
                <Input className={field} placeholder="Barcode (or leave blank)" data-testid="guard-part-barcode"
                  value={part.barcode} onChange={(e) => setPart({ ...part, barcode: e.target.value })} />
                <select className="border border-slate-300 bg-white p-2 text-sm" data-testid="guard-part-risk"
                  value={part.risk} onChange={(e) => setPart({ ...part, risk: e.target.value, household_usable: e.target.value === "high" })}>
                  <option value="high">High risk — useful at home</option>
                  <option value="normal">Normal</option>
                </select>
              </div>
              <Button size="sm" disabled={busy} className="mt-2 rounded-none" data-testid="guard-part-add"
                onClick={() => act(() => api.post("/parts-guard/parts", { ...part, unit_value: Number(part.unit_value) || 0 }), "Part on the register")}>
                <Plus size={14} className="mr-1" /> Add the part
              </Button>
            </section>
          )}

          <section className="border border-border bg-white p-4" data-testid="guard-issue">
            <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
              <Barcode size={18} weight="duotone" className="text-[#002FA7]" /> Sign a kit out
            </h2>
            <div className="mt-3 grid gap-2 sm:grid-cols-4">
              <select className="border border-slate-300 bg-white p-2 text-sm" data-testid="guard-issue-station"
                value={issue.station_id} onChange={(e) => setIssue({ ...issue, station_id: e.target.value })}>
                <option value="">Station…</option>
                {(data.stations || []).map((s) => <option key={s.id} value={s.id}>{s.name}{s.line ? ` · ${s.line}` : ""}</option>)}
              </select>
              <select className="border border-slate-300 bg-white p-2 text-sm" data-testid="guard-issue-operator"
                value={issue.operator_id} onChange={(e) => setIssue({ ...issue, operator_id: e.target.value })}>
                <option value="">Operator…</option>
                {(data.people || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select className="border border-slate-300 bg-white p-2 text-sm" data-testid="guard-issue-shift"
                value={issue.shift} onChange={(e) => setIssue({ ...issue, shift: e.target.value })}>
                {data.shifts.map((s) => <option key={s} value={s}>{s} shift</option>)}
              </select>
              <Input className={field} placeholder="Job reference" data-testid="guard-issue-job"
                value={issue.job_ref} onChange={(e) => setIssue({ ...issue, job_ref: e.target.value })} />
            </div>

            <div className="mt-3">
              <BinScanner onCode={addByBarcode} />
            </div>

            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {(data.parts || []).slice(0, 40).map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 border border-border p-2"
                  data-testid={`guard-part-row-${p.id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[#0F172A]">
                      {p.name}
                      {p.risk === "high" && <span className="ml-2 bg-[#FFE9E6] px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wider text-[#FF3B30]">high risk</span>}
                    </p>
                    <p className="text-xs text-slate-500">{p.barcode} · {p.symbol}{p.unit_value} per {p.unit}</p>
                  </div>
                  <Input type="number" min="0" className={`${field} w-20`} data-testid={`guard-kit-qty-${p.id}`}
                    value={kit[p.id] || ""} onChange={(e) => setKit({ ...kit, [p.id]: e.target.value })} />
                </div>
              ))}
            </div>
            <Button disabled={busy} className="mt-3 rounded-none" data-testid="guard-issue-submit"
              onClick={() => act(async () => {
                if (!kitLines().length) throw { response: { data: { detail: "Put something in the kit first" } } };
                await api.post("/parts-guard/issues", { ...issue, lines: kitLines() });
                setKit({});
              }, "Kit signed out")}>
              Sign it out to them
            </Button>
          </section>

          <section className="border border-border bg-white p-4" data-testid="guard-shortages">
            <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
              <Warning size={18} weight="duotone" className="text-[#FF3B30]" /> Shortage reports
            </h2>
            {(data.shortages || []).length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">No shortages reported. Keep the count going.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {data.shortages.map((s) => (
                  <div key={s.id} className="border border-border p-3" data-testid={`guard-shortage-${s.id}`}>
                    <p className="text-sm font-semibold text-[#0F172A]">
                      {s.operator_name} · {s.station_name} · {s.shift} shift · {s.day}
                    </p>
                    <p className="mt-1 text-xs text-slate-600">
                      {s.lines.map((l) => `${l.missing} ${l.unit} ${l.name} (${l.reason})`).join(" · ")}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-[#FF3B30]">
                      {s.symbol || sym}{s.value} · {s.status.replace("_", " ")}
                      {s.high_risk ? " · high-risk parts" : ""}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {(s.status === "open" || s.status === "investigating") && (
                        <Input className={`${field} max-w-xs`} placeholder="What came of it"
                          data-testid={`guard-shortage-note-${s.id}`} value={note[s.id] || ""}
                          onChange={(e) => setNote({ ...note, [s.id]: e.target.value })} />
                      )}
                      {data.statuses.filter((st) => st !== "open" && st !== s.status
                        && (s.status === "open" || s.status === "investigating")).map((st) => (
                        <Button key={st} size="sm" variant="outline" className="rounded-none"
                          disabled={busy} data-testid={`guard-shortage-${st}-${s.id}`}
                          onClick={() => act(() => api.post(`/parts-guard/shortages/${s.id}/status`, {
                            status: st, note: note[s.id] || "",
                          }), `Marked ${st.replace("_", " ")}`)}>
                          {st.replace("_", " ")}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="border border-border bg-white p-4" data-testid="guard-draw-requests">
            <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
              <Package size={18} weight="duotone" className="text-[#FF9500]" /> Over-allowance requests
            </h2>
            {(data.draw_requests || []).length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">Nothing waiting on you.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {data.draw_requests.map((d) => (
                  <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 border border-border p-3"
                    data-testid={`guard-request-${d.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">
                        {d.name} — {d.qty} {d.unit} {d.part_name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {d.taken_this_month} already this month against {d.allowance}/month
                        {d.job_ref ? ` · job ${d.job_ref}` : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" disabled={busy} className="rounded-none" data-testid={`guard-request-approve-${d.id}`}
                        onClick={() => act(() => api.post(`/parts-guard/draws/${d.id}/decide`, { approve: true, note: "" }), "Approved")}>
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy} className="rounded-none" data-testid={`guard-request-refuse-${d.id}`}
                        onClick={() => act(() => api.post(`/parts-guard/draws/${d.id}/decide`, { approve: false, note: "Over the monthly allowance" }), "Refused")}>
                        Refuse
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(data.heavy_users || []).length > 0 && (
              <div className="mt-4" data-testid="guard-heavy-users">
                <p className="overline text-slate-500">Abnormal usage this month</p>
                {data.heavy_users.map((h, i) => (
                  <p key={i} className="mt-1 text-sm text-slate-700" data-testid={`guard-heavy-${i}`}>
                    {h.name} — {h.qty} {h.unit} {h.part} against an allowance of {h.allowance}
                    {" "}({h.symbol}{h.value})
                  </p>
                ))}
              </div>
            )}
          </section>

          <section className="border border-border bg-white p-4" data-testid="guard-personal-watch">
            <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
              <Eye size={18} weight="duotone" className="text-[#002FA7]" /> Keep a personal eye on someone
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Every kit signed out to them, and every shortage on their shift, comes straight to
              you. Only you see your own watch.
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              <select className="border border-slate-300 bg-white p-2 text-sm" data-testid="guard-watch-person"
                value={watch.subject_id} onChange={(e) => setWatch({ ...watch, subject_id: e.target.value })}>
                <option value="">Who…</option>
                {(data.people || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <Input className={field} placeholder="Why (kept private to you)" data-testid="guard-watch-reason"
                value={watch.reason} onChange={(e) => setWatch({ ...watch, reason: e.target.value })} />
              <Button size="sm" disabled={busy} className="rounded-none" data-testid="guard-watch-add"
                onClick={() => act(() => api.post("/parts-guard/watch", { ...watch, active: true }), "On your watch")}>
                Watch them
              </Button>
            </div>
            <div className="mt-3 space-y-2">
              {(data.watching || []).map((w) => (
                <div key={w.id} className="flex items-center justify-between gap-2 border border-border p-3"
                  data-testid={`guard-watching-${w.subject_id}`}>
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">{w.subject_name}</p>
                    <p className="text-xs text-slate-500">{w.reason || "No reason noted"}</p>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-none" disabled={busy}
                    data-testid={`guard-watch-stop-${w.subject_id}`}
                    onClick={() => act(() => api.post("/parts-guard/watch", { subject_id: w.subject_id, reason: w.reason || "", active: false }), "Stopped watching")}>
                    Stop
                  </Button>
                </div>
              ))}
            </div>
          </section>

          <section className="border border-border bg-white p-4" data-testid="guard-watchlist">
            <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
              <UsersThree size={18} weight="duotone" className="text-[#002FA7]" /> Repeat shortages
            </h2>
            {(data.watchlist || []).length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">
                Nobody is coming up short repeatedly.
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {data.watchlist.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2 border border-border p-3"
                    data-testid={`guard-watch-${p.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">
                        {p.name}
                        {p.flagged && <span className="ml-2 bg-[#FFE9E6] px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wider text-[#FF3B30]">flagged</span>}
                      </p>
                      <p className="text-xs text-slate-500">
                        {p.reports} report(s) · {p.unexplained} unaccounted for · {sym}{p.value} · last {p.last}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
